// ControlEvent → engine command (P0.6, deleted at P2.12, **restored at P3.6**).
//
// Architecture §3's audio plane, in one file: "events that alter sound are translated to engine
// commands, stamped with AudioContext time, and written to the SAB command ring". This is the
// translation. The stamping and the writing are the deck's; the ring is P0.3's.
//
// ## Why this file existed, died, and came back
//
// P0.6 wrote it against the tone generator, so every entry in it was a tone opcode — a frequency in
// Hz, a gain against the tone's ceiling. P2.12 retired the tone generator and deleted the table
// rather than rewriting it into something no caller had asked for, which was right: a mapping layer
// with nothing to map is a shape somebody guessed at. **From P2.12 to P3.6 the deck's command ring
// carried exactly one opcode and nothing streamed it**, so the deck wrote `SET_RATE` directly and
// the seam existed only as a memory.
//
// FR-05's pitch fader is the first control that streams, so the table comes back with a real caller
// and a real shape. Three things ride on it beyond the fader itself:
//
//   * **The seam a MIDI controller reuses at FF-1.** Architecture §3: a MIDI source "emits the same
//     ControlEvents and reuses the entire mapping layer". A pointer drag and a jog wheel arrive here
//     identically, and neither gets a private route to the ring.
//   * **FR-39's remapping UI.** A `Map` of ids to descriptors can be edited, serialised and
//     persisted. A `switch` can be none of those, which is why P2.8's verify and this one both ask
//     for data rather than a branch.
//   * **NFR-P1 becomes measurable in the field.** Architecture §4.5's latency probe fills from
//     commands crossing the ring, and until now the only thing writing to it was `/bench`. A fader
//     under a finger writes hundreds of commands a minute from a real session.
//
// ## What the table does not hold
//
// State. The pitch *range* (FR-05's ±8/16/50%) is a user setting and the nudge is a held button, and
// both change what a fader position means. They arrive as a `PitchContext` argument rather than
// living here, because a table that remembered things could not be serialised and would stop being
// data the moment it did.

import {
  CROSSFADER_CURVE,
  CROSSFADER_CURVE_CODE,
  EQ_BAND,
  EQ_MAX_GAIN,
  MIXER_OPCODE,
  eqGainFromPosition,
  masterGainFromPosition,
  packEqTarget,
  stripIndexForDeck,
  trimGainFromPosition,
} from '../../platform/types/mixer';
import {
  FX_DIVISION,
  FX_DIVISIONS,
  FX_UNIT,
  packFxTarget,
} from '../../platform/types/fx';

/**
 * Which FX slot the mixer surface exposes (P4.8).
 *
 * The strip holds `FX_SLOTS_PER_STRIP` of them and the panel draws one, because P4.8 ships one unit
 * to put in them. Written down once here so P4.9 adding the second is a constant and a row of JSX
 * rather than four control ids to find and change.
 */
const FX_SLOT_ON_SURFACE = 0;

/**
 * How many steps the unit selector has above `NONE` — echo and reverb, so two (P4.9).
 *
 * The selector rides a 0…1 control like every other, so a position of 0, 0.5 or 1 is a code of 0, 1
 * or 2. Derived from the enum rather than written as a literal, so adding FR-17's "[S] extended FX"
 * moves one number.
 */
const FX_UNIT_POSITIONS = Object.keys(FX_UNIT).length - 1;
import { DECK_IDS, type ControlId, type DeckId } from '../../platform/types/control';
import { DECK_MAX_RATE, DECK_OPCODE, type DeckOpcode } from '../../platform/types/deck';

/**
 * What crosses into the audio thread: numeric, absolute, and meaning exactly one thing.
 *
 * The contrast with `ControlEvent` is the whole point of the split. A control event is a normalised
 * 0…1 from *some* source with no opinion about what the engine does with it; this is a value in the
 * engine's own units aimed at a specific target. Strings never make this journey — the audio thread
 * must not allocate, and an id is a string.
 */
export interface EngineCommand {
  readonly opcode: DeckOpcode;
  /**
   * Which deck. 0 is A, 1 is B — `DECK_TARGET`, and the same numbering `SIGNAL_SOURCE` uses.
   *
   * ⚠️ **Nothing reads it, and it is written correctly anyway (P3.7).** Each deck owns its own
   * command ring, so a command's ring already says which deck it is for and `deck-processor.ts`
   * never looks at `TARGET`. The field is in `COMMAND_RECORD` for the controls where a deck is not
   * enough — a hot-cue slot, an EQ band, a pad — and filling it with a plausible zero for deck B
   * would be a lie waiting for the first consumer.
   */
  readonly target: number;
  /** Absolute, in the engine's units — for `SET_RATE`, frames of track per frame of output. */
  readonly value: number;
}

/**
 * FR-05's three ranges, as fractions.
 *
 * "±8% default (±16%, ±50% in settings)". The percentages are the convention every piece of DJ
 * hardware has used since the SL-1200, and they are ranges rather than a continuous setting for the
 * reason the hardware has them: a fader's *resolution* is what you are choosing. ±8% over the same
 * travel is six times finer than ±50%, and beatmatching by ear needs the fine one.
 */
export const PITCH_RANGES = [0.08, 0.16, 0.5] as const;
export const DEFAULT_PITCH_RANGE = 0.08;

/**
 * How far a nudge bends the rate while it is held, as a fraction.
 *
 * 4% is roughly what a thumb against a spinning platter does, and it is deliberately large next to
 * the ±8% fader: a nudge is for shoving a beat back into place over a second or two, not for setting
 * a tempo. **It is a bend, not a second fader** — FR-05's word — so it multiplies whatever the fader
 * is doing and vanishes when the button comes up.
 */
export const PITCH_NUDGE = 0.04;

/** Which way a held nudge is bending, or zero for neither. */
export type PitchBend = -1 | 0 | 1;

/**
 * The state a fader position is meaningless without.
 *
 * Deliberately passed in rather than stored. Both fields are owned by the UI — one is a setting, one
 * is which button is down — and a mapping table that owned them would be a mapping table nobody
 * could serialise for FR-39.
 */
export interface PitchContext {
  /** One of `PITCH_RANGES`. */
  readonly rangeFraction: number;
  readonly bend: PitchBend;
}

/**
 * A fader position → a playback rate.
 *
 * **Linear in *percentage*, which is what a DJ's ear and every piece of hardware expect.** Centre is
 * exactly 1.0 — the position a fader spends most of its life at, and the one that has to be
 * reachable exactly rather than approximately, because "nearly 1.0" is a track that drifts out of
 * sync over four minutes.
 *
 * Not the exponential taper P0.6 used for the tone generator's frequency. That was right there and
 * would be wrong here: an octave is a musical interval and equal slider distances should be equal
 * intervals, but a pitch fader spans ±8% — a seventh of a semitone — where the difference between
 * linear and exponential is far below what anyone can hear, and linear is what the printed scale on
 * every deck in the world says.
 */
export function normalizedToRate(normalized: number, rangeFraction: number): number {
  const clamped = Number.isFinite(normalized)
    ? Math.min(1, Math.max(0, normalized))
    : 0.5;
  return 1 + (clamped * 2 - 1) * rangeFraction;
}

/** The inverse, for a UI that has a rate and wants to know where the fader sits. */
export function rateToNormalized(rate: number, rangeFraction: number): number {
  if (rangeFraction <= 0) {
    return 0.5;
  }
  return Math.min(1, Math.max(0, (rate - 1) / (2 * rangeFraction) + 0.5));
}

/** The rate as a percentage, for FR-05's readout. Centre reads exactly `0.0`. */
export function ratePercent(rate: number): number {
  return (rate - 1) * 100;
}

interface Mapping {
  readonly opcode: DeckOpcode;
  readonly target: number;
  readonly toEngineValue: (normalized: number, context: PitchContext) => number;
}

function pitchToRate(normalized: number, context: PitchContext): number {
  const rate = normalizedToRate(normalized, context.rangeFraction);
  // The bend multiplies rather than adds, so a 4% nudge is 4% *of what the deck is doing* whether
  // the fader sits at +8% or -8%. Adding would make a nudge mean different things at each end.
  const bent = rate * (1 + context.bend * PITCH_NUDGE);
  // Clamped at the receiving side too (`deck-processor.ts`), and clamped here so a UI reading this
  // value back sees the number the deck will actually use.
  return Math.min(DECK_MAX_RATE, Math.max(-DECK_MAX_RATE, bent));
}

/**
 * The controls that reach the engine, and the two that shape them without being commands themselves.
 *
 * The nudges are on the control bus — a MIDI controller must be able to drive them at FF-1, and
 * that means they need ids — but they map to no engine command of their own. They change the
 * `PitchContext`, and the fader's command is re-sent through the table. That is what "a temporary
 * bend, not a second fader" means expressed as plumbing.
 */
export const PITCH_CONTROL = {
  a: {
    FADER: 'deck.a.pitch',
    NUDGE_UP: 'deck.a.nudge.up',
    NUDGE_DOWN: 'deck.a.nudge.down',
  },
  b: {
    FADER: 'deck.b.pitch',
    NUDGE_UP: 'deck.b.nudge.up',
    NUDGE_DOWN: 'deck.b.nudge.down',
  },
} as const satisfies Record<DeckId, Record<string, ControlId>>;

/**
 * Deck → `COMMAND_RECORD.TARGET`. The same numbering as `SIGNAL_SOURCE`, deliberately.
 *
 * They are different rings and could disagree without anything breaking today, which is exactly why
 * they should not: two numbering schemes for "which deck" is the kind of difference that costs an
 * afternoon the first time a value is read through the wrong one.
 */
const DECK_TARGET: Record<DeckId, number> = { a: 0, b: 1 };

/**
 * The table. **Data, not code** — which is FR-39's whole seam and what this prompt's verify asks for.
 *
 * A `Map`, not an object literal, and that is a correctness choice rather than a style one:
 * `{}['constructor']` is `Object.prototype.constructor`, a function rather than `undefined`, so an
 * object-literal lookup sails past its own `undefined` check and crashes on the property access. FR-39
 * makes control ids **user data** — a remapping UI writes them — so this is reachable, not theoretical.
 * A `Map` has no inherited keys and the question never arises.
 *
 * **Both decks since P3.7**, matching `transport-controls.ts`: one more row, which is what
 * threading `deckId` through since P2.1 was for. The ids it is keyed by needed more than that —
 * `PITCH_CONTROL` was written flat and had to become deck-indexed, the same small shape change
 * `TRANSPORT_CONTROL` needed and for the same reason.
 */
const MAPPINGS = new Map<string, Mapping>(
  // One row per deck, walked from `DECK_IDS` — the second row is the whole of what P3.7 needed
  // here, exactly as P3.6 predicted. See `PITCH_CONTROL` for the part that needed more than a row.
  DECK_IDS.map((deck): [string, Mapping] => [
    PITCH_CONTROL[deck].FADER,
    {
      opcode: DECK_OPCODE.SET_RATE,
      target: DECK_TARGET[deck],
      toEngineValue: pitchToRate,
    },
  ]),
);

/** Every control the engine currently answers to. Enumerated from the table so FR-40's cheat sheet
 * and FR-39's remapping UI cannot drift from it. */
export const MAPPED_CONTROL_IDS = [...MAPPINGS.keys()] as readonly ControlId[];

/**
 * The engine command for a control, or null if this control does not affect sound.
 *
 * Null is ordinary rather than an error: Architecture §3 splits the bus at the point of consumption,
 * and plenty of control events (a transport press, a nudge, opening a panel) live entirely on the
 * control plane. `toTransportEvent` returns null for the fader for the mirror-image reason.
 */
export function toEngineCommand(
  id: ControlId,
  normalized: number,
  context: PitchContext,
): EngineCommand | null {
  const mapping = MAPPINGS.get(id);
  if (mapping === undefined) {
    return null;
  }

  return {
    opcode: mapping.opcode,
    target: mapping.target,
    value: mapping.toEngineValue(normalized, context),
  };
}

/** Does this control reach the engine at all? For a UI that wants to show what is live without
 * duplicating the table. */
export function isMappedControl(id: ControlId): boolean {
  return MAPPINGS.has(id);
}

// ---------------------------------------------------------------------------
// The mixer's controls (P4.10)
// ---------------------------------------------------------------------------

/**
 * FR-14's controls, as control ids.
 *
 * ⚠️ **Every mixer control goes through the bus, and P4.10's rule says why:** *"a control wired
 * directly to the engine is a control MIDI can never reach."* FF-1's MIDI learn is a *source* on this
 * bus, so a knob that called `AudioGraph.setTrim` from its `onChange` would work perfectly and be
 * unmappable forever. The same argument the pitch fader has obeyed since P3.6.
 *
 * Deck-indexed from the start, because `PITCH_CONTROL` was written flat, had to become deck-indexed
 * at P3.7, and this file records that as a lesson rather than repeating it. The strip index comes
 * from `stripIndexForDeck`, so the deck→strip mapping has one definition.
 */
export const MIXER_CONTROL = {
  a: {
    TRIM: 'mixer.a.trim',
    EQ_LOW: 'mixer.a.eq.low',
    EQ_MID: 'mixer.a.eq.mid',
    EQ_HIGH: 'mixer.a.eq.high',
    FILTER: 'mixer.a.filter',
    FADER: 'mixer.a.fader',
    /** FR-17's FX slot 0 (P4.8). Slot 1 exists in the engine and has no surface yet — P4.9. */
    FX_MIX: 'mixer.a.fx.mix',
    FX_DIVISION: 'mixer.a.fx.division',
    FX_FEEDBACK: 'mixer.a.fx.feedback',
    FX_UNIT: 'mixer.a.fx.unit',
    FX_DECAY: 'mixer.a.fx.decay',
    FX_DAMPING: 'mixer.a.fx.damping',
  },
  b: {
    TRIM: 'mixer.b.trim',
    EQ_LOW: 'mixer.b.eq.low',
    EQ_MID: 'mixer.b.eq.mid',
    EQ_HIGH: 'mixer.b.eq.high',
    FILTER: 'mixer.b.filter',
    FADER: 'mixer.b.fader',
    /** FR-17's FX slot 0 (P4.8). Slot 1 exists in the engine and has no surface yet — P4.9. */
    FX_MIX: 'mixer.b.fx.mix',
    FX_DIVISION: 'mixer.b.fx.division',
    FX_FEEDBACK: 'mixer.b.fx.feedback',
    FX_UNIT: 'mixer.b.fx.unit',
    FX_DECAY: 'mixer.b.fx.decay',
    FX_DAMPING: 'mixer.b.fx.damping',
  },
} as const satisfies Record<DeckId, Record<string, ControlId>>;

/**
 * The controls that belong to the mixer rather than to a channel (P4.5).
 *
 * Separate from `MIXER_CONTROL` above because that table is keyed by deck and these are not: there
 * is **one** crossfader, and giving it a per-deck id would be inventing two of an instrument the
 * hardware has one of.
 */
export const MIXER_MASTER_CONTROL = {
  CROSSFADER: 'mixer.crossfader',
  CROSSFADER_CURVE: 'mixer.crossfader.curve',
  /** FR-16's master gain (P4.6). There is no limiter control — see `limiter.ts`. */
  MASTER_GAIN: 'mixer.master.gain',
} as const satisfies Record<string, ControlId>;

/** A command for the **mixer's** ring, which is not the deck's — see `MIXER_MAPPINGS`. */
export interface MixerCommand {
  readonly opcode: number;
  readonly target: number;
  readonly value: number;
}

interface MixerMapping {
  readonly opcode: number;
  readonly target: number;
  readonly toEngineValue: (normalized: number) => number;
}

/**
 * The mixer's half of the table.
 *
 * ⚠️ **A second table rather than rows in the first, because they address different rings.**
 * `toEngineCommand` produces a command for a *deck's* ring; the mixer has its own ring, its own
 * opcode vocabulary and its own target encoding. Merging them would mean one function returning
 * commands for two destinations, and the first bug from that would be a trim written into a deck's
 * ring as a rate.
 *
 * This is the same split Architecture §3 already makes at the point of consumption: `toEngineCommand`
 * and `toTransportEvent` both return null for events that are not theirs, and this makes a third.
 *
 * A `Map` for the reason the deck's table is a `Map`: control ids are user data once FR-39's
 * remapping UI exists, and `{}['constructor']` is not `undefined`.
 */
const MIXER_MAPPINGS = new Map<string, MixerMapping>(
  DECK_IDS.flatMap((deck): [string, MixerMapping][] => {
    const strip = stripIndexForDeck(deck);
    return [
      [
        MIXER_CONTROL[deck].TRIM,
        {
          opcode: MIXER_OPCODE.SET_TRIM,
          target: strip,
          toEngineValue: trimGainFromPosition,
        },
      ],
      [
        MIXER_CONTROL[deck].FADER,
        {
          opcode: MIXER_OPCODE.SET_FADER,
          target: strip,
          // Identity, for `FILTER`'s reason: `faderGainFromPosition` is the shared law and the
          // audio thread applies it, because the crossfader has to be multiplied in over there.
          toEngineValue: (normalized) => normalized,
        },
      ],
      // FR-17's FX, slot 0 (P4.8). All four address the same slot, so they share one packed target
      // — low byte the strip, high byte the slot. `FX_SLOT_ON_SURFACE` is the one place that 0 is
      // written down, so adding P4.9's second slot to the panel is a constant rather than a search.
      [
        MIXER_CONTROL[deck].FX_MIX,
        {
          opcode: MIXER_OPCODE.SET_FX_MIX,
          target: packFxTarget(strip, FX_SLOT_ON_SURFACE),
          // Identity, for `FILTER`'s reason: `fxMixGains` is the shared law and the audio thread
          // applies it, so the wire carries the knob rather than a pair of gains.
          toEngineValue: (normalized) => normalized,
        },
      ],
      [
        MIXER_CONTROL[deck].FX_FEEDBACK,
        {
          opcode: MIXER_OPCODE.SET_FX_FEEDBACK,
          target: packFxTarget(strip, FX_SLOT_ON_SURFACE),
          // Identity: `echoFeedbackFromPosition` owns the ceiling and the worklet applies it, so a
          // stale bundle cannot send a coefficient this side thinks is safe and that side does not.
          toEngineValue: (normalized) => normalized,
        },
      ],
      [
        MIXER_CONTROL[deck].FX_DIVISION,
        {
          opcode: MIXER_OPCODE.SET_FX_DIVISION,
          target: packFxTarget(strip, FX_SLOT_ON_SURFACE),
          // ⚠️ **Not identity.** A selector's 0…1 position is meaningless on the wire; the division
          // is. `fxDivisionFromValue` snaps whatever arrives to the nearest legal fraction, so the
          // command carries the musical meaning rather than an index into a list that might be
          // reordered on one side of the ring and not the other.
          toEngineValue: (normalized) =>
            FX_DIVISIONS[
              Math.min(
                FX_DIVISIONS.length - 1,
                Math.max(0, Math.round(normalized * (FX_DIVISIONS.length - 1))),
              )
            ] ?? FX_DIVISION.HALF,
        },
      ],
      [
        MIXER_CONTROL[deck].FX_DECAY,
        {
          opcode: MIXER_OPCODE.SET_FX_DECAY,
          target: packFxTarget(strip, FX_SLOT_ON_SURFACE),
          // Identity: `reverbDecayFromPosition` is the shared law, so the UI prints seconds from the
          // same function the worklet derives its per-line feedback gains from.
          toEngineValue: (normalized) => normalized,
        },
      ],
      [
        MIXER_CONTROL[deck].FX_DAMPING,
        {
          opcode: MIXER_OPCODE.SET_FX_DAMPING,
          target: packFxTarget(strip, FX_SLOT_ON_SURFACE),
          toEngineValue: (normalized) => normalized,
        },
      ],
      [
        MIXER_CONTROL[deck].FX_UNIT,
        {
          opcode: MIXER_OPCODE.SET_FX_UNIT,
          target: packFxTarget(strip, FX_SLOT_ON_SURFACE),
          // ⚠️ **A three-way selector since P4.9, carried on a 0…1 control.** `FX_UNIT`'s codes are
          // 0/1/2, so a position is the code over two and this rounds it back. Rounded rather than
          // thresholded because the codes are a set and not a ramp; `fxUnitFromCode` on the far side
          // treats anything unrecognised as `NONE`, the only value that cannot make a noise nobody
          // asked for.
          toEngineValue: (normalized) =>
            Math.round(Math.min(1, Math.max(0, normalized)) * FX_UNIT_POSITIONS),
        },
      ],
      [
        MIXER_CONTROL[deck].FILTER,
        {
          opcode: MIXER_OPCODE.SET_FILTER,
          target: strip,
          // Identity: the knob's position *is* the value the worklet wants, because
          // `filterFromPosition` is the shared law and lives on both sides of the ring.
          toEngineValue: (normalized) => normalized,
        },
      ],
      ...(
        [
          [MIXER_CONTROL[deck].EQ_LOW, EQ_BAND.LOW],
          [MIXER_CONTROL[deck].EQ_MID, EQ_BAND.MID],
          [MIXER_CONTROL[deck].EQ_HIGH, EQ_BAND.HIGH],
        ] as const
      ).map(([id, band]): [string, MixerMapping] => [
        id,
        {
          opcode: MIXER_OPCODE.SET_EQ_BAND,
          target: packEqTarget(strip, band),
          toEngineValue: (normalized) => eqGainFromPosition(normalized, EQ_MAX_GAIN),
        },
      ]),
    ];
  }),
);

// The two controls that are the mixer's rather than a channel's. Appended rather than folded into
// the per-deck loop above, because that loop runs once per deck and these must exist exactly once.
MIXER_MAPPINGS.set(MIXER_MASTER_CONTROL.CROSSFADER, {
  opcode: MIXER_OPCODE.SET_CROSSFADER,
  // Unused by the processor — there is one crossfader — but a target is part of the record shape,
  // and 0 is the honest "not addressing a strip" rather than a strip index that means nothing.
  target: 0,
  toEngineValue: (normalized) => normalized,
});
MIXER_MAPPINGS.set(MIXER_MASTER_CONTROL.MASTER_GAIN, {
  opcode: MIXER_OPCODE.SET_MASTER_GAIN,
  target: 0,
  toEngineValue: masterGainFromPosition,
});
MIXER_MAPPINGS.set(MIXER_MASTER_CONTROL.CROSSFADER_CURVE, {
  opcode: MIXER_OPCODE.SET_CROSSFADER_CURVE,
  target: 0,
  // ⚠️ A switch riding a 0…1 control bus. Everything on Architecture §3's bus is normalised, so the
  // curve arrives as a position and is rounded to the nearer code here rather than in the worklet —
  // the same side of the ring as every other `toEngineValue`, so there is one place that knows how
  // a control's travel maps to an engine value.
  toEngineValue: (normalized) =>
    normalized >= 0.5
      ? CROSSFADER_CURVE_CODE[CROSSFADER_CURVE.SHARP]
      : CROSSFADER_CURVE_CODE[CROSSFADER_CURVE.SMOOTH],
});

/** Every mixer control the engine answers to, enumerated from the table so a cheat sheet cannot
 * drift from it. */
export const MAPPED_MIXER_CONTROL_IDS = [
  ...MIXER_MAPPINGS.keys(),
] as readonly ControlId[];

/**
 * The mixer command for a control, or null if this control is not the mixer's.
 *
 * Null is ordinary, not an error — see `toEngineCommand`, which says the same thing about the deck's
 * half of the bus.
 */
export function toMixerCommand(id: ControlId, normalized: number): MixerCommand | null {
  const mapping = MIXER_MAPPINGS.get(id);
  if (mapping === undefined) {
    return null;
  }
  return {
    opcode: mapping.opcode,
    target: mapping.target,
    value: mapping.toEngineValue(normalized),
  };
}
